/**
 * TabSwitchDetector (Sprint 6 B5).
 *
 * Detect:
 *   - `document.visibilitychange` → hidden = user switch tab hoặc minimize
 *   - `window.blur` → user click sang window khác (Alt+Tab, click app khác)
 *
 * Severity MEDIUM (không HIGH như fullscreen_exit vì có thể là accident
 * — notification popup, OS update prompt). Quá nhiều trong session → instructor
 * có quyền invalidate manual.
 *
 * Throttle: chỉ log nếu lần cuối > 2s — chống flood khi user alt-tab nhanh.
 */

import { useEffect, useRef } from "react";

interface Props {
  onViolation: (event: {
    type: "tab_switch";
    severity: "medium";
    detail?: Record<string, unknown>;
  }) => void;
}

const THROTTLE_MS = 2000;

export function TabSwitchDetector({ onViolation }: Props) {
  const lastLoggedRef = useRef(0);

  useEffect(() => {
    const maybeLog = (source: "visibilitychange" | "blur") => {
      const now = Date.now();
      if (now - lastLoggedRef.current < THROTTLE_MS) return;
      lastLoggedRef.current = now;
      onViolation({
        type: "tab_switch",
        severity: "medium",
        detail: { source, at: new Date().toISOString() },
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") maybeLog("visibilitychange");
    };
    const onBlur = () => maybeLog("blur");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [onViolation]);

  return null;
}
